import axios from "axios";
import { decodeBase64UrlToString } from "./base64url";
function isRecord(value) {
    return typeof value === "object" && value !== null;
}
const OPERATOR_PATH_PATTERNS = [
    /^\/clubs\/my-club(\/|$)/,
    /^\/clubs\/\d+\/(staff|report|entries|unpaid|user-summaries)(\/|$)/,
    /^\/clubs\/\d+\/members(?!\/(me|visible)(\/|$))(\/|$)/,
    /^\/payments\/clubs\//,
];
export function isOperatorWorkspacePath(url) {
    if (!url)
        return false;
    const path = url.split("?")[0] ?? "";
    return OPERATOR_PATH_PATTERNS.some((pattern) => pattern.test(path));
}
// WHY: the re-admission flight's own requests must neither refresh-retry nor re-trigger the advisory signal.
export function withoutRefreshRetry(config) {
    return { ...config, _retry: true, _ignoreOperatorSignal: true };
}
export class RefreshDiscardedError extends Error {
    constructor() {
        super("Token refresh discarded: the session changed while it was in flight");
        this.name = "RefreshDiscardedError";
    }
}
export class UncertainRefreshError extends Error {
    constructor(reason) {
        super(reason);
        this.name = "UncertainRefreshError";
    }
}
export class RefreshPersistenceError extends UncertainRefreshError {
    constructor(cause) {
        super("Refreshed tokens could not be persisted");
        this.name = "RefreshPersistenceError";
        this.persistenceError = cause;
    }
}
const refreshers = new WeakMap();
// WHY: joins the interceptor's single-flight refresh so a forced refresh never races a second POST.
export function refreshAccessToken(client) {
    const refresher = refreshers.get(client);
    if (!refresher) {
        return Promise.reject(new Error("refreshAccessToken: client was not created by createApiClient"));
    }
    return refresher();
}
const MOBILE_REPORTED_FLAG = "__mobileReported";
/**
 * Marks an error as already having been offered to central observability
 * (via `onHttpError`), so downstream consumers — notably the React Query
 * global error net — can avoid double-reporting the same failure. The flag
 * is non-enumerable so it never leaks into logging/serialization of the
 * error object.
 */
function markAsReported(error) {
    if (!isRecord(error)) {
        return;
    }
    Object.defineProperty(error, MOBILE_REPORTED_FLAG, {
        value: true,
        enumerable: false,
        configurable: true,
    });
}
/**
 * Checks whether `error` (or, for errors normalized into an `ApiClientError`
 * whose `originalError` is preserved, its original cause) was already marked
 * as reported by the `onHttpError` interceptor hook.
 */
export function wasAlreadyReported(error) {
    if (isRecord(error) && error[MOBILE_REPORTED_FLAG] === true) {
        return true;
    }
    if (isRecord(error) &&
        "originalError" in error &&
        isRecord(error.originalError) &&
        error.originalError[MOBILE_REPORTED_FLAG] === true) {
        return true;
    }
    return false;
}
function hasOwn(value, key) {
    return isRecord(value) && key in value;
}
function hasTokenPair(value) {
    return (hasOwn(value, "accessToken") &&
        hasOwn(value, "refreshToken") &&
        typeof value.accessToken === "string" &&
        typeof value.refreshToken === "string");
}
function hasLegacyTokenPair(value) {
    return (hasOwn(value, "AccessToken") &&
        hasOwn(value, "RefreshToken") &&
        typeof value.AccessToken === "string" &&
        typeof value.RefreshToken === "string");
}
function isRefreshResponseData(value) {
    return (hasPayload(value) ||
        hasTokenPair(value) ||
        hasLegacyTokenPair(value));
}
function hasNestedTokenPayload(value) {
    return hasOwn(value, "payload") && isRefreshResponseData(value.payload);
}
function hasPayload(response) {
    return (isRecord(response) &&
        "success" in response &&
        "payload" in response &&
        response.payload != null);
}
function isServerTokenPair(pair) {
    if (pair.refreshToken.length !== 88 ||
        /[^A-Za-z0-9+/]/.test(pair.refreshToken.slice(0, 86)) ||
        pair.refreshToken.slice(86) !== "==") {
        return false;
    }
    const segments = pair.accessToken.split(".");
    if (segments.length !== 3 ||
        segments.some((segment) => !segment || /[^A-Za-z0-9_-]/.test(segment))) {
        return false;
    }
    const payloadSegment = segments[1];
    if (!payloadSegment || payloadSegment.length % 4 === 1)
        return false;
    try {
        const payload = JSON.parse(decodeBase64UrlToString(payloadSegment));
        return (isRecord(payload) &&
            typeof payload.exp === "number" &&
            Number.isFinite(payload.exp));
    }
    catch {
        return false;
    }
}
export function createApiClient(config) {
    const { baseURL, timeout = 10000, getAccessToken, getRefreshToken, getRefreshContext, waitUntilRefreshAllowed, onTokensRefreshed, onAuthFailed, onHttpError, onOperatorPathFailure, onRefreshFailed, } = config;
    const client = axios.create({
        baseURL,
        timeout,
        headers: {
            "Content-Type": "application/json",
        },
    });
    let refreshPromise = null;
    let refreshWaiters = 0;
    const extractAuthTokens = (data) => {
        if (hasPayload(data)) {
            if (!hasNestedTokenPayload(data)) {
                throw new Error("Refresh response payload is invalid");
            }
            return extractAuthTokens(data.payload);
        }
        if (hasTokenPair(data)) {
            return data;
        }
        if (hasLegacyTokenPair(data)) {
            return {
                accessToken: data.AccessToken,
                refreshToken: data.RefreshToken,
            };
        }
        throw new Error("Refresh response missing tokens");
    };
    const refreshFailureOutcome = (error) => {
        if (error instanceof UncertainRefreshError)
            return "uncertain";
        if (!axios.isAxiosError(error))
            return "rejected";
        const status = error.response?.status;
        if (status === 401 || status === 403)
            return "terminal";
        if (status === 400 || status === 0 || !error.response || error.code === "ECONNABORTED" || error.code === "ETIMEDOUT" || error.code === "ERR_CANCELED")
            return "uncertain";
        return "rejected";
    };
    const refreshTokens = () => {
        if (!refreshPromise) {
            refreshWaiters = 1;
            const attempt = (async () => {
                await waitUntilRefreshAllowed?.();
                const context = getRefreshContext?.();
                const refreshToken = await getRefreshToken?.();
                if (!refreshToken) {
                    await onAuthFailed?.();
                    throw new Error("No refresh token available");
                }
                const response = await axios.post(`${baseURL}/auth/refresh`, {
                    refreshToken,
                }, { timeout: 20000 });
                let tokens;
                try {
                    tokens = extractAuthTokens(response.data);
                }
                catch (error) {
                    throw new UncertainRefreshError(error instanceof Error ? error.message : "invalid_refresh_response");
                }
                if (!isServerTokenPair(tokens)) {
                    throw new UncertainRefreshError("invalid_refresh_response");
                }
                const accepted = await (getRefreshContext
                    ? onTokensRefreshed?.(tokens.accessToken, tokens.refreshToken, context)
                    : onTokensRefreshed?.(tokens.accessToken, tokens.refreshToken));
                if (accepted === false) {
                    throw new RefreshDiscardedError();
                }
                return {
                    accessToken: tokens.accessToken,
                    refreshToken: tokens.refreshToken,
                };
            })();
            refreshPromise = attempt.catch((refreshError) => {
                void handleRefreshFailure(refreshError, refreshWaiters);
                throw refreshError;
            }).finally(() => {
                refreshPromise = null;
                refreshWaiters = 0;
            });
        }
        else
            refreshWaiters += 1;
        return refreshPromise;
    };
    const handleRefreshFailure = async (refreshError, waiters = 1) => {
        if (refreshError instanceof RefreshDiscardedError) {
            return;
        }
        const outcome = refreshFailureOutcome(refreshError);
        const sessionInvalidated = outcome === "terminal";
        let refreshTokenAgeMs = null;
        try {
            const accessToken = await getAccessToken?.();
            const payloadSegment = accessToken?.split(".")[1];
            if (payloadSegment) {
                const payload = JSON.parse(decodeBase64UrlToString(payloadSegment));
                if (isRecord(payload) && typeof payload.iat === "number" && Number.isFinite(payload.iat)) {
                    refreshTokenAgeMs = Math.max(0, Date.now() - payload.iat * 1000);
                }
            }
        }
        catch {
            refreshTokenAgeMs = null;
        }
        if (sessionInvalidated) {
            try {
                await onAuthFailed?.();
            }
            catch {
                // Auth cleanup must not prevent refresh-failure telemetry.
            }
        }
        try {
            await onRefreshFailed?.(refreshError, { sessionInvalidated, outcome, refreshTokenAgeMs, waiters });
        }
        catch {
            // Telemetry must never break the auth/refresh flow.
        }
    };
    refreshers.set(client, refreshTokens);
    client.interceptors.request.use(async (config) => {
        try {
            const accessToken = await getAccessToken?.();
            if (accessToken) {
                config.headers.Authorization = `Bearer ${accessToken}`;
            }
        }
        catch (error) {
            console.error("Error getting access token:", error);
        }
        return config;
    }, (error) => Promise.reject(error));
    client.interceptors.response.use((response) => response, async (error) => {
        const originalRequest = error?.config;
        const isAuthenticationRequest = originalRequest?.url?.includes("/account/login") ||
            originalRequest?.url?.includes("/auth/google") ||
            originalRequest?.url?.includes("/auth/apple");
        if ((error?.response?.status === 401 || error?.response?.status === 403) &&
            originalRequest &&
            !isAuthenticationRequest &&
            !originalRequest._retry) {
            originalRequest._retry = true;
            try {
                const { accessToken: newAccessToken } = await refreshTokens();
                originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                return client(originalRequest);
            }
            catch (refreshError) {
                return Promise.reject(refreshError);
            }
        }
        const failedStatus = error?.response?.status;
        if (onOperatorPathFailure &&
            !originalRequest?._ignoreOperatorSignal &&
            (failedStatus === 403 || failedStatus === 404) &&
            isOperatorWorkspacePath(originalRequest?.url)) {
            try {
                onOperatorPathFailure(error);
            }
            catch {
            }
        }
        if (onHttpError) {
            try {
                onHttpError(error);
            }
            catch {
                // Telemetry must never break the request pipeline.
            }
            finally {
                markAsReported(error);
            }
        }
        return Promise.reject(error);
    });
    return client;
}
export function createServerClient(baseURL, timeout = 10000) {
    return axios.create({
        baseURL,
        timeout,
        headers: {
            "Content-Type": "application/json",
        },
    });
}
export function isAxiosError(error) {
    return axios.isAxiosError(error);
}
function getString(value) {
    return typeof value === "string" ? value : undefined;
}
function normalizeApiErrorData(data) {
    if (!data) {
        return undefined;
    }
    const nestedError = data.error;
    const nestedObject = isRecord(nestedError) ? nestedError : undefined;
    const nestedString = getString(nestedError);
    const code = getString(nestedObject?.code) ?? data.code;
    const detail = getString(nestedObject?.detail) ?? data.detail;
    const message = getString(nestedObject?.message) ?? nestedString ?? data.message ?? detail;
    if (!code && !message && !detail) {
        return undefined;
    }
    return {
        code,
        message,
        detail,
    };
}
function isApiClientErrorLike(error) {
    return (hasOwn(error, "isApiClientError") &&
        error.isApiClientError === true &&
        hasOwn(error, "code") &&
        typeof error.code === "string" &&
        hasOwn(error, "message") &&
        typeof error.message === "string");
}
export function getApiError(error) {
    if (isApiClientErrorLike(error)) {
        const normalized = normalizeApiErrorData(isRecord(error.details) ? error.details : undefined);
        return {
            code: error.code || normalized?.code,
            message: error.message || normalized?.message || normalized?.detail,
            detail: normalized?.detail,
        };
    }
    if (isAxiosError(error)) {
        return (normalizeApiErrorData(error.response?.data) ?? {
            code: error.code,
            message: error.message || "Unknown error",
            detail: undefined,
        });
    }
    if (error instanceof Error) {
        return {
            code: undefined,
            message: error.message,
            detail: undefined,
        };
    }
    return undefined;
}
export function getAxiosErrorMessage(error) {
    return getApiError(error)?.message || "Unknown error";
}
export function getAxiosErrorCode(error) {
    return getApiError(error)?.code;
}
export function isNetworkError(error) {
    return isAxiosError(error) && !error.response;
}
export function isAuthError(error) {
    return isAxiosError(error) && error.response?.status === 401;
}
export function isForbiddenError(error) {
    return isAxiosError(error) && error.response?.status === 403;
}
export function isNotFoundError(error) {
    return isAxiosError(error) && error.response?.status === 404;
}
export { axios };
